// ───────────────────────────────────────────────────────────────────────────
// 🔑 A STEP-5 ASK, UNDER THE ROW IT IS ABOUT (np-step5 — Hannah's layout correction, 2026-11-01).
// (Superseded 2026-11-01 by Hannah's consolidated corrections: the ask is a PANEL nested inside its item's own card.)
// The card sits inside its own section, directly below its row: the pay-stub ask under "Most recent paystub" (Income), the
// tax / insurance asks under their rows and the VOM asks under "Verification of Mortgage" (Housing). Only the reason card
// sits at the top of step 5. While the ask is owed its row shows "Needed" with no upload of its own — this card's filled
// primary button is the single upload. Every word comes from the server (backend constants/step5AskCopy, approved).
//   · owed: the approved title, body and button (light blue — the "$… verified toward…" box's own #eff3fe / #bfdbfe);
//   · uploaded (being read): ✓ on the left, "Thanks. We’re reading your new <item>.", each file with the red Remove
//     (back to the ask — backend utils/documentRemoval → utils/step5Asks onDocumentRemoved).
// ───────────────────────────────────────────────────────────────────────────
import React, { useRef, useState } from 'react';
import axios from 'axios';
import useHeldUpload from '../../utils/useHeldUpload';
import { isUploadChecking, heldQuarantineId } from '../../constants/uploadChecking';
import { carryFollowUp } from '../../utils/uploadChecking';
import { friendlyFileName } from '../../constants/borrowerDocumentName';

const API = process.env.REACT_APP_API_URL || '';
// The one attach of a document to a property in its role (POST /rentals/:id/attach) — the Housing rows (RentalProperties)
// and a housing ask's card both file through it.
export const attachToProperty = (headers, propertyId, documentId, role) => axios.post(`${API}/api/guided-flow/rentals/${propertyId}/attach`, { documentId, role }, { headers });
export const ASK_BOX = Object.freeze({ backgroundColor: '#eff3fe', border: '1px solid #bfdbfe' });
// The page's own upload button ("Add another" / "Upload" — GuidedDocumentsStep DOC_ROW_UPLOAD_BTN): white, outlined.
export const UPLOAD = { flexShrink: 0, marginLeft: 'auto', minHeight: 36, padding: '6px 14px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#000', cursor: 'pointer', whiteSpace: 'nowrap' };
const REMOVE = { padding: '2px 8px', fontSize: 11.5, fontWeight: 700, borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#7f1d1d', cursor: 'pointer' };

/** The open ask (owed or being read) for a row: by kind, or by property + role. */
export function askFor(needs, { kinds = null, propertyId = null, role = null } = {}) {
  return (needs || []).find((n) => n && n.ask && ['pending', 'responded'].includes(String(n.status))
    && (kinds ? kinds.includes(n.ask.kind) : true)
    && (propertyId != null ? Number(n.ask.propertyId) === Number(propertyId) : true)
    && (role ? n.ask.role === role : true)) || null;
}

export default function Step5AskCard({ need, headers, onChanged, files = false }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const heldUp = useHeldUpload(onChanged);   // a 202 UPLOAD_CHECKING answer (constants/uploadChecking)
  if (!need || !need.ask) return null;
  const ask = need.ask;
  const answered = String(need.status) === 'responded';
  const upload = async (picked) => {
    if (!picked || !picked.length) return;
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('document_type', ask.docType || 'additional_need');
      fd.append('need_id', String(need.id));
      Array.from(picked).forEach((f) => fd.append('files', f));
      const up = await axios.post(`${API}/api/documents/upload`, fd, { headers });
      // Held for the virus check: not uploaded yet, nothing chained (no documentId) — the row re-reads until it lands.
      if (isUploadChecking(up)) {
        // Round 3: the property attach is carried — it runs once the check releases the file.
        if (ask.propertyId && ask.role) await carryFollowUp(API, headers, { kind: 'rental_attach', params: { propertyId: ask.propertyId, role: ask.role }, holds: [{ quarantineId: heldQuarantineId(up) }] });
        heldUp.hold(); return;
      }
      if (ask.propertyId && ask.role) {
        for (const d of (up.data && up.data.documents) || []) await attachToProperty(headers, ask.propertyId, d.id, ask.role);
      }
      if (onChanged) await onChanged();
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.error) || 'That upload did not go through. Please try again.');
    } finally { setBusy(false); }
  };
  const remove = async (f) => {
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/api/documents/${f.id}/remove`, {}, { headers });
      if (onChanged) await onChanged();
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.error) || 'That did not go through. Please try again.');
    } finally { setBusy(false); }
  };
  // 🔑 THE PANEL INSIDE THE ITEM'S OWN CARD (Hannah's consolidated corrections, 2026-11-01): below the item's description and
  // files; no heading of its own (the card's title names the item); the message and the upload — the page's own upload
  // style (white, outlined), right-aligned, under the text on a phone when it doesn't fit. After the upload: ✓ and the
  // reading line; the new file joins the card's own file list (with Remove), or — where the card has none (the VOM) — here.
  return (
    <div id={`need-card-${need.id}`} data-testid="guided-step5-need-card" data-ask={ask.kind} data-answered={answered ? 'true' : 'false'}
      style={{ ...ASK_BOX, borderRadius: 10, padding: '10px 12px', marginTop: 8, scrollMarginTop: 72, display: 'grid', gap: 6 }}>
      {!answered ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'flex-start', gap: '8px 12px' }}>
          <div data-testid="guided-step5-need-text" style={{ flex: '1 1 220px', minWidth: 0, fontSize: 13.5, color: '#000', lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{ask.body}</div>
          {ask.button && (
            <>
              <input ref={input} type="file" multiple style={{ display: 'none' }} data-testid="guided-step5-need-upload-input" onChange={(e) => upload(e.target.files)} />
              <button type="button" data-testid="guided-step5-need-upload" disabled={busy || heldUp.held} onClick={() => input.current && input.current.click()} style={UPLOAD}>
                {busy ? 'Uploading…' : ask.button}
              </button>
            </>
          )}
        </div>
      ) : (
        <div data-testid="guided-step5-need-answered" style={{ fontSize: 13, color: '#2d6a0a', fontWeight: 700, lineHeight: 1.5 }}>
          <span data-testid="guided-step5-ask-tick" style={{ marginRight: 6 }}>✓</span>{ask.reading}
        </div>
      )}
      {answered && files && (ask.files || []).map((f) => (
        <div key={f.id} data-testid="guided-step5-ask-file" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#000' }}>
          <span style={{ overflowWrap: 'anywhere' }}>{friendlyFileName(f.name)}</span>
          <button type="button" data-testid="guided-step5-ask-file-remove" disabled={busy} onClick={() => remove(f)} style={REMOVE}>Remove</button>
        </div>
      ))}
      {err && <div role="alert" style={{ fontSize: 12, color: '#9a3412' }}>{err}</div>}
      {heldUp.line && <div data-testid="upload-checking" style={{ fontSize: 12, color: '#000' }}>{heldUp.line}</div>}
    </div>
  );
}
